import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { navigateToTerminalCommand } from '@/lib/navigation'
import { useIndexData } from '@/hooks/useIndexData'
import { RefreshCw, ArrowUpDown } from 'lucide-react'
import { clsx } from 'clsx'
import TickerLink from '@/components/ui/TickerLink'
import type { IndexComponent } from '@/lib/types'

// ── Index Configs ───────────────────────────────────────────────────────────

const INDICES = [
  // Americas
  { key: 'sp500', label: 'S&P 500', short: 'SPX' },
  { key: 'nasdaq100', label: 'NASDAQ 100', short: 'NDX' },
  { key: 'dow30', label: 'DOW 30', short: 'INDU' },
  // Europe
  { key: 'ftse100', label: 'FTSE 100', short: 'UKX' },
  { key: 'dax40', label: 'DAX 40', short: 'DAX' },
  { key: 'cac40', label: 'CAC 40', short: 'CAC' },
  { key: 'ibex35', label: 'IBEX 35', short: 'IBEX' },
  { key: 'smi', label: 'SMI', short: 'SMI' },
  // Asia Pacific
  { key: 'nikkei225', label: 'Nikkei 225', short: 'NKY' },
  { key: 'hangseng', label: 'Hang Seng', short: 'HSI' },
  { key: 'csi300', label: 'CSI 300', short: 'CSI' },
  { key: 'chinext', label: 'ChiNext', short: 'CNG' },
  { key: 'star50', label: 'STAR Market', short: 'STAR' },
  { key: 'sse50', label: 'SSE 50', short: 'SSE50' },
  { key: 'kospi', label: 'KOSPI', short: 'KOSPI' },
  { key: 'sensex', label: 'BSE Sensex', short: 'SENSEX' },
  { key: 'asx200', label: 'ASX 200', short: 'ASX' },
]

type SortMode = 'alpha' | 'change' | 'change_abs'

// ── Tile Color (Bloomberg-style: blue up, red down) ─────────────────────────

function tileClasses(changePct: number): string {
  const abs = Math.abs(changePct)
  if (changePct > 0) {
    if (abs >= 3) return 'bg-blue-600 text-white'
    if (abs >= 2) return 'bg-blue-500 text-white'
    if (abs >= 1) return 'bg-blue-500/80 text-blue-50'
    if (abs >= 0.5) return 'bg-blue-500/60 text-blue-100'
    return 'bg-blue-500/40 text-blue-100'
  }
  if (changePct < 0) {
    if (abs >= 3) return 'bg-red-600 text-white'
    if (abs >= 2) return 'bg-red-500 text-white'
    if (abs >= 1) return 'bg-red-500/80 text-red-50'
    if (abs >= 0.5) return 'bg-red-500/60 text-red-100'
    return 'bg-red-500/40 text-red-100'
  }
  return 'bg-zinc-700 text-zinc-300'
}

function fmtChange(n: number): string {
  const sign = n >= 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}`
}

function fmtPct(n: number): string {
  const sign = n >= 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}%`
}

// ── Stock Tile ──────────────────────────────────────────────────────────────

function StockTile({
  comp,
  flashDirection,
  onClick,
}: {
  comp: IndexComponent
  flashDirection: 'up' | 'down' | undefined
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex flex-col items-center justify-center px-1 py-1.5 rounded-sm',
        'transition-all duration-300 ease-in-out',
        'hover:brightness-125 hover:z-10 cursor-pointer border border-transparent',
        'min-w-[72px] min-h-[44px]',
        tileClasses(comp.change_pct),
        flashDirection === 'up' && 'animate-bloomberg-flash-green ring-1 ring-emerald-400/40',
        flashDirection === 'down' && 'animate-bloomberg-flash-red ring-1 ring-red-400/40',
        flashDirection === undefined && false,
      )}
      title={`${comp.name} (${comp.ticker})\nPrice: ${comp.price}\nChange: ${fmtChange(comp.change)} (${fmtPct(comp.change_pct)})`}
    >
      <span className="text-[11px] font-bold leading-none tracking-tight"><TickerLink ticker={comp.ticker} className="text-inherit hover:text-white">{comp.display_ticker || comp.ticker}</TickerLink></span>
      <span className="text-[9px] leading-none mt-0.5 opacity-90 font-mono">
        {fmtChange(comp.change)}
      </span>
    </button>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function IndexTriggerPage() {
  const { index: indexParam } = useParams<{ index: string }>()
  const navigate = useNavigate()
  const { data, prevData, loading, error, indexKey, setIndexKey, fetchIndex } = useIndexData()
  const [sort, setSort] = useState<SortMode>('alpha')
  const [flashDirections, setFlashDirections] = useState<Map<string, 'up' | 'down'>>(new Map())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Resolve index from URL param
  const activeIndex = indexParam ?? 'ftse100'

  // Sync store with URL param
  useEffect(() => {
    if (activeIndex !== indexKey) {
      setIndexKey(activeIndex)
    } else if (!data && !loading) {
      fetchIndex()
    }
  }, [activeIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 5 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchIndex()
    }, 5_000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchIndex])

  // Flash changed tiles with directional colour
  useEffect(() => {
    if (!data || !prevData) return
    const directions = new Map<string, 'up' | 'down'>()
    const prevMap = new Map(prevData.components.map(c => [c.ticker, c]))
    for (const comp of data.components) {
      const prev = prevMap.get(comp.ticker)
      if (prev && prev.price !== comp.price) {
        directions.set(comp.ticker, comp.price > prev.price ? 'up' : 'down')
      }
    }
    if (directions.size > 0) {
      setFlashDirections(directions)
      const timeout = setTimeout(() => setFlashDirections(new Map()), 1000)
      return () => clearTimeout(timeout)
    }
  }, [data, prevData])

  // Sort components
  const sorted = useMemo(() => {
    if (!data) return []
    const comps = [...data.components]
    switch (sort) {
      case 'alpha':
        return comps.sort((a, b) => a.ticker.localeCompare(b.ticker))
      case 'change':
        return comps.sort((a, b) => b.change_pct - a.change_pct)
      case 'change_abs':
        return comps.sort((a, b) => Math.abs(b.change_pct) - Math.abs(a.change_pct))
      default:
        return comps
    }
  }, [data, sort])

  const activeConfig = INDICES.find(i => i.key === activeIndex)

  const handleStockClick = useCallback((ticker: string) => {
    navigateToTerminalCommand(navigate, `/quote ${ticker}`)
  }, [navigate])

  return (
    <div className="flex flex-col h-full bg-base">
      {/* ── Header Bar (Bloomberg-style) ─────────────────────────────────── */}
      <header className="glass px-4 py-3 shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Index tabs */}
          <div className="flex items-center gap-1">
            {INDICES.map(idx => (
              <button
                key={idx.key}
                onClick={() => navigate(`/app/indices/${idx.key}`)}
                className={clsx(
                  'px-3 py-1.5 rounded text-xs font-bold transition-colors',
                  activeIndex === idx.key
                    ? 'bg-orange-500 text-white'
                    : 'bg-surface-card text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                )}
              >
                {idx.short}
              </button>
            ))}
          </div>

          {/* Index value display */}
          {data && (
            <div className="flex items-center gap-6 text-xs font-mono">
              <div>
                <span className="text-text-muted mr-1">{activeConfig?.label}</span>
                <span className="text-text-primary font-bold text-sm">{(data.index_value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={data.index_change >= 0 ? 'text-blue-400' : 'text-red-400'}>
                <span>{fmtChange(data.index_change)}</span>
                <span className="ml-1">({fmtPct(data.index_change_pct)})</span>
              </div>
              <div className="text-text-muted">
                <span>H: {(data.high ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                <span className="ml-3">L: {(data.low ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Sort */}
            <div className="flex items-center gap-1">
              <ArrowUpDown size={12} className="text-text-muted" />
              {[
                { key: 'alpha' as SortMode, label: 'A-Z' },
                { key: 'change' as SortMode, label: '%' },
                { key: 'change_abs' as SortMode, label: '|%|' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => setSort(s.key)}
                  className={clsx(
                    'px-2 py-1 rounded text-[10px] font-bold transition-colors',
                    sort === s.key
                      ? 'bg-surface-hover text-text-primary'
                      : 'text-text-muted hover:text-text-primary',
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => fetchIndex()}
              className="p-1.5 rounded hover:bg-surface-hover text-text-muted hover:text-text-primary transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>

            {data && (
              <span className="text-[10px] text-text-muted font-mono">
                {new Date(data.timestamp).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* ── Grid ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-3">
        {loading && !data && (
          <div className="space-y-3">
            <div className="grid gap-[2px]" style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
            }}>
              {Array.from({ length: 60 }).map((_, i) => (
                <div key={i} className="skeleton min-h-[44px] !rounded-sm" />
              ))}
            </div>
            <p className="text-text-muted text-sm text-center">Loading {activeConfig?.label} components...</p>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64">
            <p className="text-rose-400 text-sm">{error}</p>
          </div>
        )}

        {data && sorted.length > 0 && (
          <div className="grid gap-[2px]" style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
          }}>
            {sorted.map(comp => (
              <StockTile
                key={comp.ticker}
                comp={comp}
                flashDirection={flashDirections.get(comp.ticker)}
                onClick={() => handleStockClick(comp.ticker)}
              />
            ))}
          </div>
        )}

        {data && sorted.length === 0 && (
          <div className="flex items-center justify-center h-64">
            <p className="text-text-muted text-sm">No component data available.</p>
          </div>
        )}
      </div>

      {/* ── Legend ────────────────────────────────────────────────────────── */}
      <div className="px-4 py-1.5 border-t border-border flex items-center gap-3 shrink-0">
        <span className="text-[9px] text-text-muted font-mono mr-1">KEY:</span>
        {[
          { label: 'Up >2%', cls: 'bg-blue-500' },
          { label: 'Up', cls: 'bg-blue-500/50' },
          { label: 'Flat', cls: 'bg-zinc-700' },
          { label: 'Down', cls: 'bg-red-500/50' },
          { label: 'Down >2%', cls: 'bg-red-500' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={clsx('w-3 h-2.5 rounded-sm', l.cls)} />
            <span className="text-[9px] text-text-muted">{l.label}</span>
          </div>
        ))}
        <span className="ml-auto text-[9px] text-text-muted font-mono">Auto-refresh: 5s</span>
      </div>
    </div>
  )
}
